import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  CreditCard, 
  Mail, 
  Settings,
  Bell,
  CalendarDays,
  Search,
  Building2,
  PhoneCall,
  LogOut,
  BarChart3,
  UserCircle,
  Trophy,
  ClipboardList,
  FileQuestion,
  MessageSquare,
  ClipboardCheck,
  Menu,
  X,
  FileText,
  Monitor,
  Activity,
  Sliders,
  Sparkles,
  Loader2,
  User,
  ChevronDown,
  MapPin,
  Mic,
  MicOff
} from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';
import StaffProfileModal from './StaffProfileModal';
import PoweredByBranding from './PoweredByBranding';
import { useBrandLogo } from '../contexts/BrandLogoContext';
import { rememberAuthenticatedPath } from '../utils/authSession';

const AdminLayout = ({ children, sidebarContent = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { brandLogo: cmsBrandLogo } = useBrandLogo();
  const [user, setUser] = useState(null);
  const [isLicensed, setIsLicensed] = useState(true);
  const [appVersion, setAppVersion] = useState('v2.1.001');
  const [managerHasAccess, setManagerHasAccess] = useState(false);
  const [isLoadingMenu, setIsLoadingMenu] = useState(true);
  const [hospitalLogo, setHospitalLogo] = useState(null);
  
  // Sidebar state management
  const isSidebarCollapsed = false; // Sidebar always expanded
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // True only when the viewport is wide enough for the AI drawer to push (not overlay) content
  const [aiPushable, setAiPushable] = useState(true);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // AI Drawer state
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false);
  const [aiChatUrl, setAiChatUrl] = useState(null);
  const [aiTrainerMode, setAiTrainerMode] = useState('iframe');
  const [aiTrainerMessages, setAiTrainerMessages] = useState([]);
  const [aiTrainerInput, setAiTrainerInput] = useState('');
  const [aiTrainerSending, setAiTrainerSending] = useState(false);
  const [aiTrainerIconUrl, setAiTrainerIconUrl] = useState(null);
  const [aiTrainerListening, setAiTrainerListening] = useState(false);
  const [aiTrainerVoiceError, setAiTrainerVoiceError] = useState('');
  const [aiTrainerInputFocused, setAiTrainerInputFocused] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiTooltip, setShowAiTooltip] = useState(false);
  const [aiSheetDragY, setAiSheetDragY] = useState(0);
  const [aiSheetDragging, setAiSheetDragging] = useState(false);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(null);
  const [mobileKeyboardInset, setMobileKeyboardInset] = useState(0);
  const [mobileViewportOffsetTop, setMobileViewportOffsetTop] = useState(0);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [isCalendarSheetOpen, setIsCalendarSheetOpen] = useState(false);
  const [calendarItems, setCalendarItems] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarMonthDate, setCalendarMonthDate] = useState(new Date());
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [isLeaderboardSheetOpen, setIsLeaderboardSheetOpen] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const aiSheetDragStartY = useRef(null);
  const aiTrainerRecognitionRef = useRef(null);

  const isStaffLikeUser = user?.role === 'Staff' || user?.role === 'Manager' || user?.role === 'Reporting Manager' || user?.role === 'ReportingManager';
  const profilePhotoUrl = user?.profile_photo_url || user?.profilePhotoUrl || null;
  const userInitial = user?.full_name?.charAt(0)?.toUpperCase() || 'U';

  const renderInlineMarkdown = (text) => {
    const parts = [];
    const pattern = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const token = match[0];
      const key = `${match.index}-${token}`;
      if (token.startsWith('**')) {
        parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
      } else if (token.startsWith('*')) {
        parts.push(<em key={key}>{token.slice(1, -1)}</em>);
      } else {
        parts.push(<code key={key}>{token.slice(1, -1)}</code>);
      }
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  };

  const renderAiTrainerMarkdown = (content) => {
    const lines = String(content || '').split(/\r?\n/);
    const nodes = [];
    let listItems = [];
    let orderedItems = [];
    let codeLines = [];
    let inCodeBlock = false;
    let pendingOrderedBlank = false;

    const parseTableRow = (line) => {
      const trimmedLine = line.trim().replace(/^\|/, '').replace(/\|$/, '');
      return trimmedLine.split('|').map(cell => cell.trim());
    };

    const isTableDivider = (line) => {
      const cells = parseTableRow(line);
      return cells.length > 1 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
    };

    const flushLists = () => {
      if (listItems.length) {
        nodes.push(<ul key={`ul-${nodes.length}`}>{listItems}</ul>);
        listItems = [];
      }
      if (orderedItems.length) {
        nodes.push(<ol key={`ol-${nodes.length}`}>{orderedItems}</ol>);
        orderedItems = [];
      }
      pendingOrderedBlank = false;
    };

    const flushCode = () => {
      if (codeLines.length) {
        nodes.push(<pre key={`code-${nodes.length}`}><code>{codeLines.join('\n')}</code></pre>);
        codeLines = [];
      }
    };

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      if (/^\s*```/.test(line)) {
        if (inCodeBlock) {
          flushCode();
          inCodeBlock = false;
        } else {
          flushLists();
          inCodeBlock = true;
        }
        continue;
      }
      if (inCodeBlock) {
        codeLines.push(line);
        continue;
      }

      const trimmed = line.trim();
      if (!trimmed) {
        if (orderedItems.length) {
          pendingOrderedBlank = true;
        } else {
          flushLists();
        }
        continue;
      }

      const orderedMatch = trimmed.match(/^(\d+)[.)]\s+(.+)/);
      if (!orderedMatch && pendingOrderedBlank) {
        flushLists();
      }

      if (
        trimmed.includes('|') &&
        lines[index + 1] &&
        isTableDivider(lines[index + 1])
      ) {
        flushLists();
        const headers = parseTableRow(trimmed);
        const rows = [];
        index += 2;
        while (index < lines.length && lines[index].trim().includes('|')) {
          const row = parseTableRow(lines[index]);
          if (row.length) {
            rows.push(row);
          }
          index += 1;
        }
        index -= 1;
        nodes.push(
          <div key={`table-wrap-${nodes.length}`} className="ai-trainer-table-wrap hide-scrollbar">
            <table className="ai-trainer-markdown-table">
              <thead>
                <tr>
                  {headers.map((header, headerIndex) => (
                    <th key={`th-${headerIndex}`}>{renderInlineMarkdown(header)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr key={`tr-${rowIndex}`}>
                    {headers.map((_, cellIndex) => (
                      <td key={`td-${rowIndex}-${cellIndex}`}>{renderInlineMarkdown(row[cellIndex] || '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }

      const bulletMatch = trimmed.match(/^[-*]\s+(.+)/);
      if (bulletMatch) {
        orderedItems = [];
        listItems.push(<li key={`li-${index}`}>{renderInlineMarkdown(bulletMatch[1])}</li>);
        continue;
      }

      if (orderedMatch) {
        listItems = [];
        pendingOrderedBlank = false;
        orderedItems.push(<li key={`oli-${index}`} value={Number(orderedMatch[1]) || undefined}>{renderInlineMarkdown(orderedMatch[2])}</li>);
        continue;
      }

      flushLists();
      const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)/);
      if (headingMatch) {
        const level = Math.min(headingMatch[1].length, 4);
        const HeadingTag = `h${level + 2}`;
        nodes.push(<HeadingTag key={`h-${index}`}>{renderInlineMarkdown(headingMatch[2])}</HeadingTag>);
      } else {
        nodes.push(<p key={`p-${index}`}>{renderInlineMarkdown(trimmed)}</p>);
      }
    }

    flushLists();
    flushCode();
    return nodes.length ? nodes : content;
  };

  const handleProfileUpdated = (updatedUser) => {
    if (!updatedUser) return;
    setUser((currentUser) => {
      const mergedUser = { ...(currentUser || {}), ...updatedUser };
      localStorage.setItem('user', JSON.stringify(mergedUser));
      return mergedUser;
    });
  };

  const getHomePathForUser = (currentUser = user) => {
    const role = currentUser?.role;
    if (role === 'Staff') {
      return currentUser?.is_reporting_manager ? '/manager/landing' : '/staff/dashboard';
    }
    if (role === 'Manager' || role === 'Reporting Manager' || role === 'ReportingManager') {
      return '/manager/landing';
    }
    if (role === 'CRM Officer' || role === 'CRM Associate') {
      return '/crm';
    }
    if (role === 'ExlvaAdminLite') {
      return '/training';
    }
    if (role === 'Super Admin' || role === 'Admin' || role === 'Exlva Admin' || role === 'ExlvaAdmin') {
      return '/super-admin';
    }
    return '/dashboard';
  };

  const handleLogoHome = (event) => {
    event?.preventDefault?.();
    setIsMobileDrawerOpen(false);
    const homePath = getHomePathForUser();
    if (location.pathname === homePath) {
      window.location.reload();
      return;
    }
    navigate(homePath);
  };

  const logoHomeProps = {
    onClick: handleLogoHome,
    onKeyDown: (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        handleLogoHome(event);
      }
    },
    role: 'button',
    tabIndex: 0
  };

  // Profile dropdown state
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileDropdownRef = useRef(null);

  // Theme settings
  const [themeSettings, setThemeSettings] = useState({
    sidebar_bg_color: '#FFFFFF',
    header_bg_color: '#FFFFFF',
    content_bg_color: '#FFFFFF',
    chat_bg_color: '#FFFFFF'
  });

  // Detect mobile viewport + whether the AI drawer can push content without cropping it
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      // The AI drawer (380px) should only PUSH the main content when there is enough
      // room to keep training content readable (sidebar ~268 + ~640 content + 380 drawer).
      // Below that, it overlays the content instead of compressing it.
      setAiPushable(window.innerWidth >= 1280);
      // Close drawer when switching from mobile to desktop
      if (window.innerWidth >= 768) {
        setIsMobileDrawerOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) {
      rememberAuthenticatedPath(location.pathname, location.search);
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const updateVisualViewportHeight = () => {
      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height || window.innerHeight;
      const keyboardInset = visualViewport
        ? Math.max(0, Math.round(window.innerHeight - visualViewport.height - visualViewport.offsetTop))
        : 0;
      setMobileViewportHeight(viewportHeight);
      setMobileKeyboardInset(keyboardInset);
      setMobileViewportOffsetTop(Math.round(visualViewport?.offsetTop || 0));
    };

    updateVisualViewportHeight();
    window.visualViewport?.addEventListener('resize', updateVisualViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateVisualViewportHeight);
    window.addEventListener('resize', updateVisualViewportHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateVisualViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateVisualViewportHeight);
      window.removeEventListener('resize', updateVisualViewportHeight);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (aiTrainerRecognitionRef.current) {
        aiTrainerRecognitionRef.current.onend = null;
        aiTrainerRecognitionRef.current.abort();
      }
    };
  }, []);



  // Toggle mobile drawer
  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setManagerHasAccess(parsedUser.has_evaluation_access || false);
    } else if (token) {
      const restoreUser = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const restoredUser = await response.json();
            localStorage.setItem('user', JSON.stringify(restoredUser));
            setUser(restoredUser);
            setManagerHasAccess(restoredUser.has_evaluation_access || false);
          }
        } catch (error) {
          console.error('Failed to restore user session:', error);
        }
      };
      restoreUser();
    }
    fetchAppVersion();
    fetchHospitalLogo();
    fetchThemeSettings();
  }, []);

  // Session heartbeat: ping server every 2 minutes to keep session alive
  // The heartbeat upserts a session record so the Active Users KPI works correctly.
  // Sessions with no heartbeat for 10 minutes are auto-cleaned as stale.
  // The heartbeat also returns a refreshed JWT token to create a sliding session.
  useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    let consecutiveFailures = 0;
    const sendHeartbeat = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          consecutiveFailures = 0;
          // Save the refreshed token to extend session
          const data = await res.json();
          if (data.token) {
            localStorage.setItem('token', data.token);
          }
        } else if (res.status === 401) {
          consecutiveFailures++;
          // After 2 consecutive 401s, try token refresh endpoint
          if (consecutiveFailures >= 2) {
            try {
              const refreshRes = await fetch(`${BACKEND_URL}/api/auth/refresh-token`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
              });
              if (refreshRes.ok) {
                const refreshData = await refreshRes.json();
                if (refreshData.token) {
                  localStorage.setItem('token', refreshData.token);
                  consecutiveFailures = 0;
                  return;
                }
              }
            } catch (e) { /* refresh failed */ }
            // Token is truly expired beyond recovery — redirect without destroying
            // local state unless the user explicitly logs out.
            window.location.href = '/signin';
          }
        }
      } catch (e) { /* ignore network failures */ }
    };

    // Send initial heartbeat immediately (also re-creates session if missing)
    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 120000); // every 2 minutes

    // NOTE: We intentionally do NOT use beforeunload to call /logout.
    // beforeunload also fires on page refresh, which would kill the session
    // even though the user is still logged in. Instead, stale sessions are
    // automatically cleaned after 10 minutes of no heartbeat.

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, []);

  const fetchAppVersion = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/version`);
      const data = await response.json();
      setAppVersion(data.version || 'v2.1.001');
    } catch (error) {
      console.error('Failed to fetch app version:', error);
      setAppVersion('v2.1.001');
    }
  };

  const fetchHospitalLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/hospital/logo`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.logo_url) {
          setHospitalLogo(data.logo_url);
        }
      }
    } catch (error) {
      console.error('Failed to fetch hospital logo:', error);
    }
  };

  const fetchThemeSettings = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/cms/theme-settings`);
      if (response.ok) {
        const data = await response.json();
        setThemeSettings({
          sidebar_bg_color: data.sidebar_bg_color || '#FFFFFF',
          header_bg_color: data.header_bg_color || '#FFFFFF',
          content_bg_color: data.content_bg_color || '#FFFFFF',
          chat_bg_color: data.chat_bg_color || '#FFFFFF'
        });
      }
    } catch (error) {
      console.error('Failed to fetch theme settings:', error);
    }
  };

  // Check license status for Hospital Admin
  useEffect(() => {
    const checkLicense = async () => {
      if (!user) {
        // Keep isLoadingMenu true until user data is loaded from localStorage.
        // This prevents the menu items from flashing before the role is resolved.
        return;
      }

      if (user.role !== 'Hospital Admin') {
        setIsLicensed(true); // Non-Hospital Admins have full access
        setIsLoadingMenu(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/subscriptions/check-license`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setIsLicensed(data.is_licensed);
        } else {
          setIsLicensed(false); // Default to not licensed on error
        }
      } catch (error) {
        console.error('Failed to check license status:', error);
        setIsLicensed(false); // Default to not licensed on error
      } finally {
        setIsLoadingMenu(false); // Menu data is ready
      }
    };

    checkLicense();
  }, [user]);

  // Check manager evaluation access
  useEffect(() => {
    const checkManagerAccess = async () => {
      // Check for Staff with is_reporting_manager flag OR actual Manager/Reporting Manager role
      const isManager = user && (
        (user.role === 'Staff' && user.is_reporting_manager) ||
        user.role === 'Manager' ||
        user.role === 'Reporting Manager' ||
        user.role === 'ReportingManager'
      );
      
      if (!isManager) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/evaluations/manager/check-access`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          // Update user data in localStorage with evaluation access
          const updatedUser = { ...user, has_evaluation_access: data.has_access };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          // Update state to trigger re-render
          setManagerHasAccess(data.has_access);
          setUser(updatedUser);
        }
      } catch (error) {
        console.error('Failed to check manager evaluation access:', error);
      }
    };

    checkManagerAccess();
  }, [user?.id]); // Only depend on user.id to avoid infinite loops

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      if (token) {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
    localStorage.removeItem('last_authenticated_path');
    navigate('/signin');
  };

  // Open AI Drawer - authenticate and load chat
  const handleOpenAiDrawer = async () => {
    if (isAiDrawerOpen) {
      setIsAiDrawerOpen(false);
      setAiChatUrl(null);
      setAiTrainerMessages([]);
      setAiTrainerIconUrl(null);
      return;
    }

    setIsNotificationPanelOpen(false);
    setIsCalendarSheetOpen(false);
    setIsLeaderboardSheetOpen(false);
    setAiLoading(true);
    try {
      let token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to use AI Trainer');
        return;
      }
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

      // Helper to attempt the authenticate call
      const tryAuthenticate = async (authToken) => {
        return await axios.post(
          `${BACKEND_URL}/api/chat/ai-trainer/session`,
          {},
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      };

      let response;
      try {
        response = await tryAuthenticate(token);
      } catch (error) {
        // If 401, try refreshing the token first
        if (error.response?.status === 401) {
          try {
            const refreshRes = await axios.post(
              `${BACKEND_URL}/api/auth/refresh-token`,
              {},
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (refreshRes.data.token) {
              token = refreshRes.data.token;
              localStorage.setItem('token', token);
              response = await tryAuthenticate(token);
            } else {
              throw error;
            }
          } catch (refreshErr) {
            // Token could not be refreshed. Preserve local state; the next
            // authenticated request or explicit logout owns session cleanup.
            toast.error('Session expired. Please sign in again.');
            window.location.href = '/signin';
            return;
          }
        } else {
          throw error;
        }
      }

      if (response.data.success && response.data.mode === 'native') {
        setAiChatUrl(null);
        setAiTrainerMode('native');
        setAiTrainerIconUrl(response.data.ai_trainer_icon_url || null);
        setAiTrainerVoiceError('');
        setAiTrainerMessages([
          {
            role: 'assistant',
            content: response.data.initial_message || 'Hi, I am your AI Trainer. How can I help with your training today?'
          }
        ]);
        setIsAiDrawerOpen(true);
      } else if (response.data.success && response.data.chat_url) {
        setAiTrainerMode('iframe');
        setAiTrainerIconUrl(response.data.ai_trainer_icon_url || null);
        setAiChatUrl(response.data.chat_url);
        setIsAiDrawerOpen(true);
      } else {
        toast.error('Failed to open AI Trainer');
      }
    } catch (error) {
      console.error('Failed to open AI Trainer:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to open AI Trainer';
      toast.error(errorMsg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleCloseAiDrawer = () => {
    if (aiTrainerRecognitionRef.current) {
      aiTrainerRecognitionRef.current.onend = null;
      aiTrainerRecognitionRef.current.abort();
      aiTrainerRecognitionRef.current = null;
    }
    setAiTrainerListening(false);
    setAiTrainerVoiceError('');
    setAiTrainerInputFocused(false);
    setIsAiDrawerOpen(false);
    setAiTrainerInput('');
    setAiSheetDragY(0);
    setAiSheetDragging(false);
    aiSheetDragStartY.current = null;
  };

  const getAiTrainerSpeechLanguage = () => {
    const preferredLanguage = (
      localStorage.getItem('preferredLanguage') ||
      localStorage.getItem('staffPreferredLanguage') ||
      user?.preferred_language ||
      user?.language ||
      'English'
    ).toLowerCase();
    return preferredLanguage.includes('hin') || preferredLanguage.includes('हिं') ? 'hi-IN' : 'en-IN';
  };

  const handleToggleAiTrainerVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      const message = 'Voice input is not supported in this browser.';
      setAiTrainerVoiceError(message);
      toast.error(message);
      return;
    }

    if (aiTrainerListening && aiTrainerRecognitionRef.current) {
      aiTrainerRecognitionRef.current.stop();
      setAiTrainerListening(false);
      return;
    }

    setAiTrainerVoiceError('');
    const recognition = new SpeechRecognition();
    aiTrainerRecognitionRef.current = recognition;
    recognition.lang = getAiTrainerSpeechLanguage();
    recognition.interimResults = true;
    recognition.continuous = false;

    let committedTranscript = '';
    const baseInput = aiTrainerInput.trim();

    recognition.onstart = () => {
      setAiTrainerListening(true);
    };

    recognition.onresult = (event) => {
      let interimTranscript = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const transcript = event.results[index][0]?.transcript || '';
        if (event.results[index].isFinal) {
          committedTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      const spokenText = `${committedTranscript}${interimTranscript}`.trim();
      setAiTrainerInput([baseInput, spokenText].filter(Boolean).join(baseInput && spokenText ? ' ' : ''));
    };

    recognition.onerror = (event) => {
      const denied = event.error === 'not-allowed' || event.error === 'service-not-allowed';
      const message = denied
        ? 'Microphone access is blocked. Please allow microphone permission to use voice input.'
        : 'Voice input could not capture audio. Please try again.';
      setAiTrainerVoiceError(message);
      setAiTrainerListening(false);
      toast.error(message);
    };

    recognition.onend = () => {
      setAiTrainerListening(false);
      aiTrainerRecognitionRef.current = null;
    };

    recognition.start();
  };

  const sendAiTrainerMessage = async () => {
    const message = aiTrainerInput.trim();
    if (!message || aiTrainerSending) return;
    const nextMessages = [...aiTrainerMessages, { role: 'user', content: message }];
    setAiTrainerMessages(nextMessages);
    setAiTrainerInput('');
    setAiTrainerSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/chat/ai-trainer/message`,
        {
          message,
          conversation: aiTrainerMessages,
          training_context: {
            preferred_language: localStorage.getItem('preferredLanguage') || localStorage.getItem('staffPreferredLanguage') || 'English'
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiTrainerMessages(prev => [...prev, { role: 'assistant', content: response.data?.message || 'I could not generate a response. Please try again.' }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI Trainer could not generate a response');
      setAiTrainerMessages(prev => [...prev, { role: 'assistant', content: 'I could not connect to AI Trainer right now. Please try again shortly.' }]);
    } finally {
      setAiTrainerSending(false);
    }
  };

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/crm/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      // Staff notification records are optional today; keep the bell functional with an empty state.
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  };

  const handleNotificationBellClick = async () => {
    setShowAiTooltip(false);
    setIsCalendarSheetOpen(false);
    setIsLeaderboardSheetOpen(false);
    if (isAiDrawerOpen) handleCloseAiDrawer();
    const nextOpen = !isNotificationPanelOpen;
    setIsNotificationPanelOpen(nextOpen);
    if (nextOpen) {
      await fetchNotifications();
    }
  };

  const fetchCalendarItems = async () => {
    setCalendarLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/staff/profile/completed-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarItems(response.data?.completed_topics || []);
    } catch (error) {
      setCalendarItems([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleOpenCalendarSheet = async () => {
    setIsNotificationPanelOpen(false);
    setIsLeaderboardSheetOpen(false);
    if (isAiDrawerOpen) handleCloseAiDrawer();
    setAiSheetDragY(0);
    setAiSheetDragging(false);
    setIsCalendarSheetOpen(true);
    await fetchCalendarItems();
  };

  const fetchLeaderboardData = async () => {
    setLeaderboardLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/staff/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeaderboardData(response.data);
    } catch (error) {
      setLeaderboardData(null);
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleOpenLeaderboardSheet = async () => {
    setIsNotificationPanelOpen(false);
    setIsCalendarSheetOpen(false);
    if (isAiDrawerOpen) handleCloseAiDrawer();
    setAiSheetDragY(0);
    setAiSheetDragging(false);
    setIsLeaderboardSheetOpen(true);
    await fetchLeaderboardData();
  };

  useEffect(() => {
    if (isStaffLikeUser && user?.id) {
      fetchNotifications();
    }
  }, [user?.id, isStaffLikeUser]);

  const formatCalendarDate = (dateValue) => {
    if (!dateValue) return 'Date unavailable';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Date unavailable';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getCalendarItemDateKey = (item) => {
    const value = item?.completed_at || item?.due_date || item?.assigned_at || item?.scheduled_at;
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 10);
  };

  const buildCalendarDays = () => {
    const year = calendarMonthDate.getFullYear();
    const month = calendarMonthDate.getMonth();
    const first = new Date(year, month, 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return date;
    });
  };

  const activityDateKeys = new Set(calendarItems.map(getCalendarItemDateKey).filter(Boolean));
  const selectedCalendarItems = selectedCalendarDate
    ? calendarItems.filter(item => getCalendarItemDateKey(item) === selectedCalendarDate)
    : calendarItems;

  const handleAiSheetPointerDown = (event) => {
    if (!isMobile) return;
    aiSheetDragStartY.current = event.clientY;
    setAiSheetDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleAiSheetPointerMove = (event) => {
    if (!isMobile || aiSheetDragStartY.current === null) return;
    const deltaY = Math.max(0, event.clientY - aiSheetDragStartY.current);
    setAiSheetDragY(deltaY);
  };

  const handleAiSheetPointerUp = (event) => {
    if (!isMobile) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (aiSheetDragY > 100) {
      if (isAiDrawerOpen) handleCloseAiDrawer();
      setIsCalendarSheetOpen(false);
      setIsLeaderboardSheetOpen(false);
      setAiSheetDragY(0);
      setAiSheetDragging(false);
      aiSheetDragStartY.current = null;
    } else {
      setAiSheetDragY(0);
      setAiSheetDragging(false);
      aiSheetDragStartY.current = null;
    }
  };

  const renderStandardSheetHeader = ({ title, subtitle, onClose }) => (
    <>
      <div
        className={`justify-center pt-2 pb-1 ${isMobile ? 'flex' : 'hidden'}`}
        onPointerDown={handleAiSheetPointerDown}
        onPointerMove={handleAiSheetPointerMove}
        onPointerUp={handleAiSheetPointerUp}
        onPointerCancel={handleAiSheetPointerUp}
        style={{ touchAction: 'none' }}
      >
        <div className="h-1.5 w-12 rounded-full bg-gray-300" />
      </div>
      <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-100 shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="popup-close-button"
          aria-label={`Close ${title}`}
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
      </div>
    </>
  );

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Define menu items based on user role
  const getMenuItems = (userRole, isLicensed) => {
    // Debug logging
    console.log('AdminLayout - User Role:', userRole);
    console.log('AdminLayout - Is Licensed:', isLicensed);
    
    // Super Admin - Full access to all modules
    if (userRole === 'Super Admin') {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/super-admin' },
        { id: 'leads-crm', label: 'Leads', icon: ClipboardList, path: '/crm' },
        { id: 'hospitals', label: 'Hospitals', icon: Building2, path: '/hospitals' },
        { id: 'subscriptions', label: 'Subscription', icon: CreditCard, path: '/subscriptions/manage' },
        { id: 'staff', label: 'Default Org Workflow', icon: Users, path: '/hospital/staff-management' },
        { id: 'training', label: 'Training Chapters', icon: BookOpen, path: '/training' },
        { id: 'assessments', label: 'Question Bank', icon: FileQuestion, path: '/assessments' },
        { id: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/feedback' },
        { id: 'evaluations', label: 'Evaluation Form', icon: ClipboardCheck, path: '/hospital/evaluations' },
        { id: 'demo', label: 'Demo Requests', icon: PhoneCall, path: '/demo' },
        { id: 'email', label: 'Email Templates', icon: Mail, path: '/admin/email-templates' },
        { id: 'cms', label: 'CMS Settings', icon: Settings, path: '/admin/cms-settings' },
        { id: 'onboarding', label: 'Onboarding Settings', icon: ClipboardList, path: '/admin/onboarding-settings' },
        { id: 'exlva-admins', label: 'Eptral Admins', icon: UserCircle, path: '/admin/exlva-admins' },
        { id: 'integration-settings', label: 'Integration Settings', icon: Sliders, path: '/admin/integration-settings' },
        { id: 'locations', label: 'Location Master', icon: MapPin, path: '/admin/locations' },
      ];
    }
    
    // Exlva Admin - No CRM Management, No Demo, No Exlva Admin Management
    if (['ExlvaAdmin', 'Exlva Admin', 'Admin'].includes(userRole)) {
      return [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/super-admin' },
        { id: 'hospitals', label: 'Hospitals', icon: Building2, path: '/hospitals' },
        { id: 'staff', label: 'Default Org Workflow', icon: Users, path: '/hospital/staff-management' },
        { id: 'training', label: 'Training Chapters', icon: BookOpen, path: '/training' },
        { id: 'assessments', label: 'Question Bank', icon: FileQuestion, path: '/assessments' },
        { id: 'feedback', label: 'Feedback', icon: MessageSquare, path: '/feedback' },
        { id: 'evaluations', label: 'Evaluation Form', icon: ClipboardCheck, path: '/hospital/evaluations' },
        { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard, path: '/subscriptions/manage' },
        { id: 'email', label: 'Email Templates', icon: Mail, path: '/admin/email-templates' },
        { id: 'cms', label: 'CMS Settings', icon: Settings, path: '/admin/cms-settings' },
      ];
    }
    
    // Exlva Admin Lite - Restricted access (Training Chapters, Question Bank, Email Templates only)
    if (userRole === 'ExlvaAdminLite') {
      return [
        { id: 'training', label: 'Training Chapters', icon: BookOpen, path: '/training' },
        { id: 'assessments', label: 'Question Bank', icon: FileQuestion, path: '/assessments' },
        { id: 'email', label: 'Email Templates', icon: Mail, path: '/admin/email-templates' },
      ];
    }
    
    // CRM Officer and CRM Associate - CRM focused with extended access
    if (['CRM Officer', 'CRM Associate'].includes(userRole)) {
      return [
        { id: 'crm', label: 'Leads', icon: Users, path: '/crm' },
        { id: 'hospitals', label: 'Hospitals', icon: Building2, path: '/hospitals' },
        { id: 'demo-requests', label: 'Demo Requests', icon: Monitor, path: '/demo-requests' },
        { id: 'contact-submissions', label: 'Contact Submissions', icon: MessageSquare, path: '/contact-submissions' },
      ];
    }
    
    // Hospital Admin - 6 tabs (A & C moved to Reports Dashboard, Attendance temporarily hidden)
    if (userRole === 'Hospital Admin') {
      return [
        { id: 'reports', label: 'Dashboard', icon: BarChart3, path: '/hospital/reports' },
        { id: 'subscription', label: 'Subscription', icon: CreditCard, path: '/subscription' },
        { id: 'staff', label: 'Staff Management', icon: Users, path: '/hospital/staff-management' },
        // { id: 'attendance', label: 'Attendance', icon: ClipboardList, path: '/hospital/attendance-upload' }, // Temporarily hidden
        { id: 'evaluations', label: 'Staff Evaluation', icon: ClipboardCheck, path: '/hospital/evaluations' },
        { id: 'training', label: 'Training Chapters', icon: BookOpen, path: '/training' },
        { id: 'assessments', label: 'Question Bank', icon: FileQuestion, path: '/assessments' },
      ];
    }
    
    // Staff - Check if they're a Reporting Manager or regular staff
    if (userRole === 'Staff') {
      // If user has is_reporting_manager flag, show Dashboard first
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (userData.is_reporting_manager) {
        const managerMenuItems = [
          { id: 'manager', label: 'Dashboard', icon: LayoutDashboard, path: '/manager/landing' },
          { id: 'leaderboard', label: 'Leaderboard', icon: Trophy, path: '/staff/leaderboard' },
        ];
        
        // Check if manager has assigned evaluations using state
        if (managerHasAccess || userData.has_evaluation_access) {
          managerMenuItems.splice(1, 0, { id: 'evaluations', label: 'Evaluation', icon: ClipboardCheck, path: '/manager/evaluations' });
        }
        
        return managerMenuItems;
      }
      // Regular staff - only My Training and Leaderboard
      return [
        { id: 'training', label: 'Training', icon: BookOpen, path: '/staff/dashboard' },
        { id: 'leaderboard', label: 'Leaderboard', icon: Trophy, path: '/staff/leaderboard' },
      ];
    }
    
    // Reporting Manager - Team management (support multiple variations for legacy compatibility)
    // Left sidebar only shows Dashboard and Leaderboard - Evaluation Form is in top tabs
    if (userRole === 'Reporting Manager' || userRole === 'Manager' || userRole === 'ReportingManager') {
      return [
        { id: 'manager', label: 'Dashboard', icon: LayoutDashboard, path: '/manager/landing' },
        { id: 'leaderboard', label: 'Leaderboard', icon: Trophy, path: '/staff/leaderboard' },
      ];
    }
    
    // Default fallback - return empty array if no role matches
    console.warn('AdminLayout - No menu items found for role:', userRole);
    return [];
  };

  const menuItems = user ? getMenuItems(user.role, isLicensed) : [];

  const isActive = (path) => {
    if (path === '/super-admin' || path === '/dashboard') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const mobileBottomNavActive = (() => {
    if (isAiDrawerOpen) return 'ai-trainer';
    if (isCalendarSheetOpen) return 'calendar';
    if (isLeaderboardSheetOpen || location.pathname === '/staff/leaderboard') return 'leaderboard';
    return 'dashboard';
  })();

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: themeSettings.content_bg_color }}>
      {/* Mobile Overlay */}
      {isMobile && isMobileDrawerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleMobileDrawer}
        />
      )}

      {/* Sidebar - Responsive with Expand/Collapse */}
      <aside 
        className={`
          flex flex-col fixed h-full z-50
          transition-all duration-150 ease-in-out
          ${isMobile 
            ? `${isMobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'} w-[268px]` 
            : `${isSidebarCollapsed ? 'w-20' : 'w-[268px]'}`
          }
        `}
        style={{ backgroundColor: themeSettings.sidebar_bg_color }}
      >
        {/* Logo Header Area - Matches top header bar */}
        <div className="p-4 flex items-center justify-between min-h-[82px] relative z-10 border-b border-gray-200" style={{ backgroundColor: themeSettings.header_bg_color }}>
          {/* Logo - Always visible, adapts to sidebar state */}
          <div className="flex items-center overflow-visible">
            {isSidebarCollapsed && !isMobile ? (
              // Collapsed state: Show compact logo
              hospitalLogo ? (
                <img 
                  src={hospitalLogo} 
                  alt="Hospital Logo" 
                  className="w-10 h-10 object-contain cursor-pointer flex-shrink-0 rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  {...logoHomeProps}
                />
              ) : (
                <div 
                  className="w-10 h-10 bg-[#0056B3] rounded-lg flex items-center justify-center cursor-pointer flex-shrink-0 shadow-sm hover:shadow-md transition-shadow"
                  {...logoHomeProps}
                >
                  <span className="text-white font-bold text-xl select-none">E</span>
                </div>
              )
            ) : (
              // Expanded state: Show full logo
              hospitalLogo ? (
                <img 
                  src={hospitalLogo} 
                  alt="Hospital Logo" 
                  className="w-auto max-w-full object-contain cursor-pointer flex-shrink-0 ml-2.5 mt-2.5"
                  style={{ height: '43px', maxWidth: '180px', display: 'block' }}
                  {...logoHomeProps}
                />
              ) : (
                <img 
                  src={cmsBrandLogo} 
                  alt="Eptral.Hospital Logo" 
                  className="w-auto max-w-full object-contain cursor-pointer flex-shrink-0 ml-2.5 mt-2.5"
                  style={{ height: '43px', display: 'block' }}
                  {...logoHomeProps}
                />
              )
            )}
          </div>
          

          
          {/* Mobile Close Button */}
          {isMobile && (
            <button
              onClick={toggleMobileDrawer}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Navigation Menu or Custom Sidebar Content */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto app-sidebar-scroll">
          {sidebarContent ? (
            // Render custom sidebar content (e.g., leaderboard)
            <div className="px-3">
              {sidebarContent}
            </div>
          ) : isLoadingMenu ? (
            // Loading skeleton
            <>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className={`w-full flex items-center ${isSidebarCollapsed && !isMobile ? 'justify-center' : 'space-x-3'} px-3 py-3 rounded-lg bg-gray-100 animate-pulse`}
                >
                  <div className="w-5 h-5 bg-gray-300 rounded flex-shrink-0"></div>
                  {(!isSidebarCollapsed || isMobile) && (
                    <div className="h-4 bg-gray-300 rounded flex-1"></div>
                  )}
                </div>
              ))}
            </>
          ) : (
            menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <div key={item.id} className="relative group">
                  <button
                    onClick={() => {
                      navigate(item.path);
                      if (isMobile) toggleMobileDrawer();
                    }}
                    className={`w-full flex items-center ${isSidebarCollapsed && !isMobile ? 'justify-center' : 'space-x-3'} px-3 py-3 rounded-lg transition-all duration-150 ${
                      active 
                        ? 'bg-[#0056B3] text-white' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-tooltip={isSidebarCollapsed && !isMobile ? item.label : ''}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {(!isSidebarCollapsed || isMobile) && (
                      <span className="font-medium text-sm">{item.label}</span>
                    )}
                  </button>
                  
                  {/* Tooltip for collapsed sidebar */}
                  {isSidebarCollapsed && !isMobile && (
                    <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 whitespace-nowrap z-50 pointer-events-none">
                      {item.label}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* Sidebar Footer */}
        {(!isSidebarCollapsed || isMobile) && (
          <div className="p-3 border-t border-gray-200">
            <div className="text-center space-y-1">
              {/* Line 1: Terms & Privacy */}
              <div className="text-[11px] text-gray-500">
                <span 
                  className="hover:text-[#0056B3] cursor-pointer transition-colors"
                  onClick={() => window.open('/terms-and-conditions', '_blank')}
                >
                  Terms & Conditions
                </span>
                <span className="mx-1">|</span>
                <span 
                  className="hover:text-[#0056B3] cursor-pointer transition-colors"
                  onClick={() => window.open('/privacy-policy', '_blank')}
                >
                  Privacy Policy
                </span>
              </div>
              
              {/* Line 2: Version & Copyright */}
              <div className="text-[9px] text-gray-400">
                <span>{appVersion}</span>
                <span className="mx-1">|</span>
                <span>©{new Date().getFullYear()} Eptral.Hospital</span>
              </div>
              
              {/* Line 3: Powered by */}
              <div className="text-[11px] text-gray-500">
                <PoweredByBranding
                  className="max-w-full"
                  linkClassName="hover:text-[#0056B3] transition-colors max-w-[86px]"
                  textClassName="font-medium"
                  logoClassName="max-h-4 max-w-[74px]"
                />
              </div>
            </div>
          </div>
        )}
        

      </aside>

      {/* Main Content Area - Responsive */}
      <div 
        className={`flex-1 flex flex-col overflow-hidden transition-all duration-150 ease-in-out ${
          isMobile 
            ? 'ml-0' 
            : isSidebarCollapsed 
              ? 'ml-20' 
              : 'ml-[268px]'
        }`}
      >
        {/* Top Header */}
        <header className="app-auth-header sticky top-0 z-40 shrink-0 border-b border-gray-200 px-2 sm:px-3 md:px-6 flex items-center min-h-[68px] md:min-h-[82px] overflow-visible" style={{ backgroundColor: themeSettings.header_bg_color }}>
          <div className="flex justify-between items-center w-full min-w-0 gap-2">
            {/* Mobile Hamburger Menu + Logo */}
            {isMobile && (
              <div className="flex items-center min-w-0 flex-1">
                {!isStaffLikeUser && (
                  <button
                    onClick={toggleMobileDrawer}
                    className="h-9 w-9 shrink-0 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center"
                  >
                    <Menu className="w-5 h-5 text-gray-700" />
                  </button>
                )}
                {/* Mobile Logo */}
                {hospitalLogo ? (
                  <img 
                    src={hospitalLogo} 
                    alt="Logo" 
                    className={`h-8 object-contain cursor-pointer max-w-[132px] min-w-0 ${isStaffLikeUser ? 'ml-0' : 'ml-1.5'}`}
                    {...logoHomeProps}
                  />
                ) : (
                  <img 
                    src={cmsBrandLogo} 
                    alt="Eptral.Hospital Logo" 
                    className={`h-7 object-contain cursor-pointer max-w-[138px] min-w-0 ${isStaffLikeUser ? 'ml-0' : 'ml-1.5'}`}
                    {...logoHomeProps}
                  />
                )}
              </div>
            )}
            
            {/* Right Side - AI, Notifications, and User */}
            <div className="flex items-center gap-1.5 md:gap-3 ml-auto shrink-0 max-w-[52vw] sm:max-w-none">
              {/* AI Trainer entry point */}
              {isStaffLikeUser && (
                <div className="relative md:static">
                  <button
                    type="button"
                    onClick={handleNotificationBellClick}
                    className="staff-mobile-header-bubble md:hidden h-10 w-10 rounded-full bg-white hover:bg-white active:bg-white focus:bg-white border border-gray-200 flex items-center justify-center shrink-0 relative p-0 shadow-none active:shadow-none focus:outline-none focus:ring-0"
                    aria-label="Open notifications"
                  >
                    <Bell className="w-5 h-5 text-gray-700" />
                    {notifications.some(n => !n.is_read) && (
                      <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                    )}
                  </button>
                  {isNotificationPanelOpen && (
                    <>
                      <div className="fixed inset-0 z-30 md:hidden" onClick={() => setIsNotificationPanelOpen(false)} />
                      <div className="notification-panel fixed right-3 top-[5.25rem] z-50 w-[min(21rem,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)] bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden md:right-6 md:top-[7.5vh] md:w-[min(24rem,calc(100vw-3rem))] md:h-[85vh] md:max-h-[calc(100vh-6rem)] md:flex md:flex-col">
                        <div className="px-4 py-3 border-b border-gray-100 bg-white flex items-center justify-between gap-3 shrink-0">
                          <p className="text-sm font-semibold text-gray-900">Notifications</p>
                          <button
                            type="button"
                            onClick={() => setIsNotificationPanelOpen(false)}
                            className="popup-close-button"
                            aria-label="Close notifications"
                          >
                            <X className="w-3.5 h-3.5 text-white" />
                          </button>
                        </div>
                        <div className="hide-scrollbar max-h-80 md:max-h-none md:flex-1 md:min-h-0 overflow-y-auto overflow-x-hidden min-w-0">
                          {notificationsLoading ? (
                            <div className="px-4 py-6 text-sm text-gray-500 text-center">Loading...</div>
                          ) : notifications.length > 0 ? (
                            notifications.map((notification, index) => (
                              <div key={notification.id || index} className="px-4 py-3 border-b border-gray-100 last:border-b-0 min-w-0">
                                <div className="flex items-start gap-2">
                                  <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${notification.is_read ? 'bg-gray-300' : 'bg-[#0056B3]'}`} />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 whitespace-normal break-words">{notification.title || notification.message || 'Notification'}</p>
                                    {notification.message && notification.title && (
                                      <p className="text-xs text-gray-600 mt-1 whitespace-normal break-words leading-relaxed">{notification.message}</p>
                                    )}
                                    {(notification.created_at || notification.timestamp || notification.createdAt) && (
                                      <p className="text-[11px] text-gray-400 mt-1">
                                        {formatCalendarDate(notification.created_at || notification.timestamp || notification.createdAt)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-8 text-sm text-gray-500 text-center">No notifications available</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {isStaffLikeUser && (
                <div className="relative ask-ai-glow-wrapper hidden md:block"
                  onMouseEnter={() => !isAiDrawerOpen && setShowAiTooltip(true)}
                  onMouseLeave={() => setShowAiTooltip(false)}
                >
                  <button
                    onClick={() => { setShowAiTooltip(false); handleOpenAiDrawer(); }}
                    disabled={aiLoading}
                    className={`h-8 md:h-10 flex items-center gap-1 px-2.5 md:px-3.5 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 select-none relative z-10 whitespace-nowrap shrink-0 ${
                      isAiDrawerOpen
                        ? 'bg-[#009B80] text-white shadow-[0_0_8px_rgba(255,212,0,0.35)]'
                        : 'bg-[#009B80]/18 text-[#008A72] border border-[#009B80]/45 hover:bg-[#009B80]/28 hover:shadow-[0_0_10px_rgba(255,212,0,0.30)] hover:scale-[1.03]'
                    }`}
                  >
                    {aiLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <>
                        <span>AI Trainer</span>
                        <span className="ai-sparkle-blink text-sm leading-none" style={{ color: '#FF6A00', textShadow: '0 0 6px rgba(255,106,0,0.6)' }}>✧</span>
                      </>
                    )}
                  </button>

                  {/* Custom Instant Tooltip */}
                  {showAiTooltip && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2.5 z-50 pointer-events-none"
                      style={{ animation: 'tooltipFadeIn 0.15s ease-out forwards' }}
                    >
                      <div className="relative inline-block bg-white text-gray-800 rounded-xl shadow-lg border border-gray-200"
                        style={{ padding: '10px 16px', minWidth: '140px', maxWidth: '260px', width: 'max-content', boxSizing: 'border-box' }}
                      >
                        <p className="text-xs font-semibold leading-tight whitespace-nowrap">AI Trainer</p>
                        <p className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">Personalized learning support</p>
                        {/* Arrow */}
                        <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-white border-l border-t border-gray-200 rotate-45" />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isStaffLikeUser && (
                <button
                  type="button"
                  onClick={handleOpenCalendarSheet}
                  className={`hidden md:flex h-10 items-center gap-2 px-3.5 rounded-full text-sm font-semibold transition-colors duration-150 whitespace-nowrap shrink-0 border shadow-none hover:shadow-none active:shadow-none focus:outline-none focus:ring-0 ${
                    isCalendarSheetOpen
                      ? 'bg-[#0056B3] text-white border-[#0056B3]'
                      : 'bg-[#0056B3]/10 text-[#0056B3] border-[#0056B3]/25 hover:bg-[#0056B3]/16'
                  }`}
                  aria-label="Open training calendar"
                >
                  <CalendarDays className="w-4 h-4" />
                  <span>Calendar</span>
                </button>
              )}

              {isStaffLikeUser && (
                <button
                  type="button"
                  onClick={handleNotificationBellClick}
                  className="hidden md:flex h-10 w-10 items-center justify-center shrink-0 relative p-0 bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent border-0 shadow-none hover:shadow-none active:shadow-none focus:outline-none focus:ring-0 text-gray-700 hover:text-[#0056B3]"
                  aria-label="Open notifications"
                >
                  <Bell className="w-5 h-5" />
                  {notifications.some(n => !n.is_read) && (
                    <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                  )}
                </button>
              )}

              {/* User Profile Dropdown */}
              {user && (
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={() => {
                      setShowAiTooltip(false);
                      setIsProfileDropdownOpen(!isProfileDropdownOpen);
                    }}
                    className="staff-mobile-profile-trigger h-10 md:h-10 w-10 sm:w-auto flex items-center justify-center sm:justify-start gap-1 md:gap-3 cursor-pointer bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent p-0 md:px-2 rounded-full sm:rounded-lg transition-colors duration-150 shadow-none active:shadow-none hover:shadow-none border border-transparent hover:border-transparent sm:hover:border-blue-200 shrink-0 focus:outline-none focus:ring-0"
                  >
                    {/* Hide text on small mobile */}
                    <div className="text-right hidden sm:block">
                      <p className="text-xs md:text-sm text-gray-600 hidden lg:block">Welcome, {user.role}</p>
                      <p className="text-xs md:text-sm font-medium text-gray-900">{user.full_name}</p>
                    </div>
                    <div className="staff-mobile-header-bubble w-10 h-10 md:w-10 md:h-10 bg-[#0056B3] rounded-full flex items-center justify-center text-white font-semibold text-base md:text-base ring-0 transition-colors duration-150 shrink-0 overflow-hidden">
                      {profilePhotoUrl ? (
                        <img
                          src={profilePhotoUrl}
                          alt={user.full_name || 'Profile'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        userInitial
                      )}
                    </div>
                    <ChevronDown className={`w-4 h-4 text-gray-500 hidden sm:block transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isProfileDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-[min(14rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 animate-in fade-in slide-in-from-top-1 duration-150 overflow-hidden">
                      {/* User info header */}
                      <div className="px-4 py-2 border-b border-gray-100 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{user.full_name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email || user.role}</p>
                      </div>

                      {/* View Profile */}
                      <button
                        onClick={() => {
                          setIsProfileDropdownOpen(false);
                          if (user.role === 'Staff' || user.role === 'Manager' || user.role === 'Reporting Manager') {
                            setShowProfileModal(true);
                          } else {
                            navigate('/profile');
                          }
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <User className="w-4 h-4 text-gray-500" />
                        View Profile
                      </button>

                      {/* Divider */}
                      <div className="my-1 border-t border-gray-100" />

                      {/* Sign out */}
                      <button
                        onClick={() => {
                          setIsProfileDropdownOpen(false);
                          handleLogout();
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 text-red-500" />
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area with AI Drawer */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Main Content - Responsive with Proper Constraints */}
          <main className={`hide-scrollbar flex-1 min-w-0 overflow-y-auto overflow-x-hidden w-full transition-all duration-300 ${isAiDrawerOpen && aiPushable && !isStaffLikeUser ? 'mr-[380px]' : ''} ${isMobile && isStaffLikeUser ? 'pb-[calc(3.25rem+env(safe-area-inset-bottom))]' : ''}`}>
            {localStorage.getItem('impersonating') === 'true' && (
              <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm font-medium sticky top-0 z-30">
                <span>
                  Viewing as: <strong>{localStorage.getItem('impersonated_hospital') || 'Hospital Admin'}</strong>
                  {localStorage.getItem('impersonated_by') && (
                    <span className="ml-2 opacity-80">— impersonated by {localStorage.getItem('impersonated_by')}</span>
                  )}
                </span>
                <button
                  onClick={() => {
                    const origToken = localStorage.getItem('original_admin_token');
                    const origUser = localStorage.getItem('original_admin_user');
                    localStorage.removeItem('impersonating');
                    localStorage.removeItem('impersonated_by');
                    localStorage.removeItem('impersonated_hospital');
                    if (origToken && origUser) {
                      localStorage.setItem('token', origToken);
                      localStorage.setItem('user', origUser);
                      localStorage.removeItem('original_admin_token');
                      localStorage.removeItem('original_admin_user');
                    }
                    window.location.href = '/hospitals';
                  }}
                  className="px-3 py-1 bg-white text-amber-700 rounded-md text-xs font-semibold hover:bg-amber-50 transition-colors"
                >
                  Exit Impersonation
                </button>
              </div>
            )}
            {children}
          </main>

          {/* AI Trainer Right-Side Drawer */}
          {isAiDrawerOpen && (
            <>
              {/* Scrim behind the overlay drawer (mobile + constrained widths) */}
              {(!aiPushable || isStaffLikeUser) && (
                <div
                  className={`fixed inset-0 z-40 ${isMobile ? 'bg-black/40 transition-opacity duration-200' : 'bg-transparent'}`}
                  onClick={handleCloseAiDrawer}
                />
              )}
              <div
                className={`${
                  isMobile
                    ? 'ai-trainer-panel staff-mobile-sheet fixed left-0 right-0 bottom-0 z-50 rounded-t-[24px] border-t border-gray-200'
                    : isStaffLikeUser
                      ? 'ai-trainer-panel fixed right-6 top-[7.5vh] w-[min(24rem,calc(100vw-3rem))] h-[85vh] max-h-[calc(100vh-6rem)] z-50 rounded-2xl border border-gray-200'
                    : !aiPushable
                      ? 'ai-trainer-panel fixed right-0 top-0 bottom-0 w-[min(380px,92vw)] z-50 border-l border-gray-200'
                      : 'ai-trainer-panel absolute right-0 top-0 bottom-0 w-[380px] z-30 border-l border-gray-200'
                } flex flex-col shadow-xl min-w-0`}
                data-keyboard-open={isMobile && mobileKeyboardInset > 24 ? 'true' : 'false'}
                data-input-focused={isMobile && aiTrainerInputFocused ? 'true' : 'false'}
                style={{ 
                  animation: isMobile ? 'aiBottomSheetIn 0.28s cubic-bezier(0.2, 0.8, 0.2, 1) forwards' : 'panelFadeIn 0.18s ease-out forwards',
                  fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                  backgroundColor: themeSettings.chat_bg_color,
                  '--ai-keyboard-inset': isMobile ? `${mobileKeyboardInset}px` : '0px',
                  bottom: isMobile ? `${mobileKeyboardInset}px` : undefined,
                  top: isMobile && mobileKeyboardInset > 24 ? `${Math.max(8, mobileViewportOffsetTop + 8)}px` : undefined,
                  height: isMobile
                    ? mobileKeyboardInset > 24
                      ? 'auto'
                      : `min(95dvh, ${Math.round((mobileViewportHeight || window.innerHeight) * 0.95)}px)`
                    : undefined,
                  maxHeight: isMobile
                    ? `calc(100dvh - max(8px, env(safe-area-inset-top)))`
                    : undefined,
                  transform: isMobile ? `translateY(${aiSheetDragY}px)` : undefined,
                  transition: isMobile && !aiSheetDragging ? 'transform 0.18s cubic-bezier(0.2, 0.8, 0.2, 1), height 0.18s ease, bottom 0.18s ease, top 0.18s ease' : 'none',
                  overflow: 'hidden',
                  willChange: isMobile ? 'transform, height' : undefined,
                }}
              >
                {(isMobile || isStaffLikeUser) && (
                  <div
                    className={`${isMobile ? 'rounded-t-[24px]' : 'rounded-t-2xl'} shrink-0 bg-white`}
                    onPointerDown={handleAiSheetPointerDown}
                    onPointerMove={handleAiSheetPointerMove}
                    onPointerUp={handleAiSheetPointerUp}
                    onPointerCancel={handleAiSheetPointerUp}
                    style={{ touchAction: isMobile ? 'none' : 'auto' }}
                  >
                    <div className={`justify-center pt-2 pb-1 ${isMobile ? 'flex' : 'hidden'}`}>
                      <div className="h-1.5 w-12 rounded-full bg-gray-300" />
                    </div>
                    <div className="flex items-center justify-between px-4 pb-3 md:pt-4 md:border-b md:border-gray-100">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-9 w-9 rounded-xl bg-[#009B80]/12 flex items-center justify-center shrink-0">
                          {aiTrainerIconUrl ? (
                            <img
                              src={aiTrainerIconUrl}
                              alt="AI Trainer"
                              className="h-full w-full object-cover rounded-xl"
                            />
                          ) : (
                            <Sparkles className="w-4 h-4 text-[#009B80]" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 leading-tight">AI Trainer</p>
                          <p className="text-xs text-gray-500 leading-tight truncate">Powered by einGenius</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCloseAiDrawer}
                        className="popup-close-button"
                        aria-label="Close AI Trainer"
                      >
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                )}
                {/* AI Trainer chat */}
                {aiTrainerMode === 'native' && (
                  <div className="ai-trainer-native-chat flex-1 min-h-0 flex flex-col bg-white">
                    <div className="ai-trainer-message-list hide-scrollbar flex-1 overflow-y-auto px-4 py-4 space-y-3 overscroll-contain">
                      {aiTrainerMessages.map((message, index) => (
                        <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`ai-trainer-message-bubble max-w-[85%] rounded-2xl px-3 py-2 text-base md:text-sm leading-6 md:leading-relaxed break-words ${
                            message.role === 'user'
                              ? 'bg-[#0056B3] text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          }`}>
                            {message.role === 'assistant' ? renderAiTrainerMarkdown(message.content) : message.content}
                          </div>
                        </div>
                      ))}
                      {aiTrainerSending && (
                        <div className="flex justify-start">
                          <div className="rounded-2xl rounded-bl-md px-3 py-2 bg-gray-100 text-gray-500 text-sm flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Thinking...
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="ai-trainer-input-bar shrink-0 border-t border-gray-100 p-3 bg-white">
                      <div className="flex items-end gap-2 min-w-0">
                        <textarea
                          value={aiTrainerInput}
                          onChange={(event) => setAiTrainerInput(event.target.value)}
                          onFocus={() => setAiTrainerInputFocused(true)}
                          onBlur={() => setAiTrainerInputFocused(false)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                              event.preventDefault();
                              sendAiTrainerMessage();
                            }
                          }}
                          placeholder="Message AI Trainer..."
                          rows={1}
                          className="ai-trainer-message-input flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0056B3] min-h-[42px] max-h-28"
                        />
                        <button
                          type="button"
                          onClick={handleToggleAiTrainerVoice}
                          disabled={aiTrainerSending}
                          className={`ai-trainer-voice-button h-[42px] w-[42px] rounded-xl flex items-center justify-center shrink-0 border transition-colors ${
                            aiTrainerListening
                              ? 'bg-red-50 border-red-200 text-red-600'
                              : 'bg-white border-gray-200 text-gray-600 hover:text-[#0056B3] hover:border-[#0056B3]/40'
                          } disabled:opacity-60 disabled:cursor-not-allowed`}
                          aria-label={aiTrainerListening ? 'Stop voice input' : 'Start voice input'}
                          title={aiTrainerListening ? 'Stop voice input' : 'Start voice input'}
                        >
                          {aiTrainerListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                        </button>
                        <button
                          type="button"
                          onClick={sendAiTrainerMessage}
                          disabled={!aiTrainerInput.trim() || aiTrainerSending}
                          className="ai-trainer-send-button h-[42px] px-4 rounded-xl bg-[#0056B3] text-white text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Send
                        </button>
                      </div>
                      {(aiTrainerListening || aiTrainerVoiceError) && (
                        <div className={`mt-2 text-xs ${aiTrainerVoiceError ? 'text-red-600' : 'text-[#0056B3]'} whitespace-normal break-words`}>
                          {aiTrainerVoiceError || 'Listening... tap the microphone again to stop.'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {aiTrainerMode === 'iframe' && aiChatUrl && (
                  <div className="ai-chat-frame flex-1 overflow-hidden relative min-h-0 min-w-0">
                    <iframe
                      src={aiChatUrl}
                      className="w-full h-full border-none"
                      title="AI Trainer"
                      allow="microphone"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile && isStaffLikeUser && (
        <nav className="staff-mobile-bottom-nav fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-gray-200 shadow-none px-1.5 pt-0.5 pb-[env(safe-area-inset-bottom)]">
          <div className="grid grid-cols-4 gap-0 max-w-md mx-auto">
            <button
              type="button"
              onClick={() => {
                setIsCalendarSheetOpen(false);
                setIsLeaderboardSheetOpen(false);
                if (isAiDrawerOpen) handleCloseAiDrawer();
                navigate('/staff/dashboard');
              }}
              className={`min-h-[50px] rounded-none flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold leading-tight transition-colors bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 shadow-none ${
                mobileBottomNavActive === 'dashboard' ? 'text-[#0056B3]' : 'text-gray-600 hover:text-gray-900'
              }`}
              aria-label="Open dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Dashboard</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCalendarSheetOpen(false);
                handleOpenLeaderboardSheet();
              }}
              className={`min-h-[50px] rounded-none flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold leading-tight transition-colors bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 shadow-none ${
                mobileBottomNavActive === 'leaderboard' ? 'text-[#0056B3]' : 'text-gray-600 hover:text-gray-900'
              }`}
              aria-label="Open leaderboard"
            >
              <Trophy className="w-5 h-5" />
              <span>Leaderboard</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setIsCalendarSheetOpen(false);
                handleOpenAiDrawer();
              }}
              className={`min-h-[50px] rounded-none flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold leading-tight transition-colors bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 shadow-none ${
                mobileBottomNavActive === 'ai-trainer' ? 'text-[#009B80]' : 'text-gray-600 hover:text-gray-900'
              }`}
              aria-label="Open AI Trainer"
            >
              <Sparkles className="w-5 h-5" />
              <span>AI Trainer</span>
            </button>
            <button
              type="button"
              onClick={handleOpenCalendarSheet}
              className={`min-h-[50px] rounded-none flex flex-col items-center justify-center gap-0.5 text-[10.5px] font-semibold leading-tight transition-colors bg-transparent hover:bg-transparent active:bg-transparent focus:bg-transparent focus:outline-none focus:ring-0 shadow-none ${
                mobileBottomNavActive === 'calendar' ? 'text-[#0056B3]' : 'text-gray-600 hover:text-gray-900'
              }`}
              aria-label="Open calendar"
            >
              <CalendarDays className="w-5 h-5" />
              <span>Calendar</span>
            </button>
          </div>
        </nav>
      )}

      {isStaffLikeUser && isCalendarSheetOpen && (
        <>
          <div className={`fixed inset-0 z-50 ${isMobile ? 'bg-black/40' : 'bg-transparent'}`} onClick={() => setIsCalendarSheetOpen(false)} />
          <div className={`calendar-panel fixed z-50 bg-white shadow-2xl overflow-hidden flex flex-col min-w-0 ${
            isMobile
              ? 'staff-mobile-sheet left-0 right-0 bottom-0 rounded-t-[24px] max-h-[95dvh]'
              : 'right-6 top-[7.5vh] w-[min(24rem,calc(100vw-3rem))] h-[85vh] max-h-[calc(100vh-6rem)] rounded-2xl border border-gray-200'
          }`}
            style={{
              transform: isMobile ? `translateY(${aiSheetDragY}px)` : undefined,
              transition: isMobile && !aiSheetDragging ? 'transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none'
            }}
          >
            {renderStandardSheetHeader({
              title: 'Training Calendar',
              subtitle: 'Completed chapter activity',
              onClose: () => setIsCalendarSheetOpen(false)
            })}
            <div className={`hide-scrollbar p-4 overflow-y-auto overflow-x-hidden flex-1 min-h-0 min-w-0 ${isMobile ? 'max-h-[calc(95dvh-76px)]' : ''}`}>
              <div className="border border-gray-200 rounded-xl p-3 mb-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <button
                    type="button"
                    onClick={() => setCalendarMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))}
                    className="h-8 w-8 rounded-full bg-transparent hover:bg-gray-100 flex items-center justify-center text-gray-600"
                    aria-label="Previous month"
                  >
                    ‹
                  </button>
                  <p className="text-sm font-semibold text-gray-900">
                    {calendarMonthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                  <button
                    type="button"
                    onClick={() => setCalendarMonthDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))}
                    className="h-8 w-8 rounded-full bg-transparent hover:bg-gray-100 flex items-center justify-center text-gray-600"
                    aria-label="Next month"
                  >
                    ›
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-semibold text-gray-500 mb-1">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {buildCalendarDays().map((date) => {
                    const key = date.toISOString().slice(0, 10);
                    const isCurrentMonth = date.getMonth() === calendarMonthDate.getMonth();
                    const isToday = key === new Date().toISOString().slice(0, 10);
                    const hasActivity = activityDateKeys.has(key);
                    const isSelected = selectedCalendarDate === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setSelectedCalendarDate(isSelected ? null : key)}
                        className={`relative h-9 rounded-lg text-xs font-semibold transition-colors ${
                          isSelected
                            ? 'bg-[#0056B3] text-white'
                            : isToday
                              ? 'bg-blue-50 text-[#0056B3]'
                              : isCurrentMonth
                                ? 'text-gray-800 hover:bg-gray-100'
                                : 'text-gray-300'
                        }`}
                      >
                        {date.getDate()}
                        {hasActivity && (
                          <span className={`absolute left-1/2 -translate-x-1/2 bottom-1 h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-[#0056B3]'}`} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              {calendarLoading ? (
                <div className="py-10 text-sm text-gray-500 text-center">Loading calendar...</div>
              ) : selectedCalendarItems.length > 0 ? (
                <div className="space-y-3">
                  {selectedCalendarItems.map((item, index) => (
                    <div key={item.topic_id || index} className="border border-gray-200 rounded-xl p-3 bg-white">
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 rounded-xl bg-blue-50 text-[#0056B3] flex items-center justify-center shrink-0">
                          <CalendarDays className="w-5 h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-900 break-words">{item.topic_name || 'Completed chapter'}</p>
                          <p className="text-xs text-gray-500 mt-1">{formatCalendarDate(item.completed_at)}</p>
                          {item.score !== null && item.score !== undefined && (
                            <p className="text-xs text-emerald-700 font-medium mt-1">Score: {item.score}%</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <CalendarDays className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm font-semibold text-gray-900">No calendar activity yet</p>
                  <p className="text-xs text-gray-500 mt-1">Completed chapters will appear here.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {isMobile && isStaffLikeUser && isLeaderboardSheetOpen && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setIsLeaderboardSheetOpen(false)} />
          <div
            className="leaderboard-panel staff-mobile-sheet fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-[24px] shadow-2xl max-h-[95dvh] overflow-hidden"
            style={{
              transform: `translateY(${aiSheetDragY}px)`,
              transition: !aiSheetDragging ? 'transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none'
            }}
          >
            {renderStandardSheetHeader({
              title: 'Leaderboard',
              subtitle: 'Institution rankings',
              onClose: () => setIsLeaderboardSheetOpen(false)
            })}
            <div className="hide-scrollbar p-4 overflow-y-auto overflow-x-hidden max-h-[calc(95dvh-76px)] min-w-0">
              {leaderboardLoading ? (
                <div className="py-10 text-sm text-gray-500 text-center">Loading leaderboard...</div>
              ) : leaderboardData?.leaderboard?.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="text-lg font-bold text-[#0056B3]">
                    {leaderboardData.config?.leaderboard_scope === 'department_wise' ? 'Department' : 'Institution-wide'} Rankings
                  </h3>
                  {leaderboardData.leaderboard.slice(0, 10).map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center justify-between gap-3 rounded-xl border p-3 bg-white ${
                        entry.user_id === leaderboardData.own_rank?.user_id ? 'border-[#0056B3] bg-blue-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 text-center shrink-0">
                          {entry.badge ? <Trophy className={`w-6 h-6 mx-auto ${entry.badge === 'Gold' ? 'text-yellow-500' : entry.badge === 'Silver' ? 'text-gray-400' : 'text-orange-600'}`} /> : <span className="text-base font-bold text-gray-400">#{entry.rank}</span>}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">{entry.name}</p>
                          <p className="text-xs text-gray-500 truncate">{entry.department}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-[#0056B3]">{entry.score}%</p>
                        {entry.badge && <p className="text-xs font-semibold text-yellow-800 bg-yellow-100 rounded-full px-2 py-0.5 mt-1">{entry.badge}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <Trophy className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-sm font-semibold text-gray-900">No leaderboard data yet</p>
                  <p className="text-xs text-gray-500 mt-1">Rankings will appear once training activity is available.</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes aiBottomSheetIn {
          from { transform: translateY(100%); opacity: 0.96; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes panelFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes aiSparkleBlink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }
        .ai-sparkle-blink {
          display: inline-block;
          animation: aiSparkleBlink 1.5s ease-in-out infinite;
        }
        .ask-ai-glow-wrapper::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 108%;
          height: 115%;
          background: transparent;
          box-shadow: 0 0 6px 2px rgba(255,212,0,0.45), 0 0 2px 1px rgba(255,212,0,0.20);
          border-radius: 9999px;
          pointer-events: none;
          z-index: 0;
          animation: askAiGlowPulse 2.5s ease-in-out infinite;
        }
        @keyframes askAiGlowPulse {
          0%, 100% { opacity: 0.45; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.85; transform: translate(-50%, -50%) scale(1.03); }
        }
        @keyframes tooltipFadeIn {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Staff Profile Modal */}
      {showProfileModal && (
        <StaffProfileModal 
          isOpen={showProfileModal} 
          onClose={() => setShowProfileModal(false)} 
          onProfileUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
};

export default AdminLayout;
